import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Menu, HeartHandshake, HelpCircle, PlayCircle, Bug, Download } from 'lucide-react';
import { UserProfileBadge } from './UserProfileBadge';
import { NotificacionesBell } from './NotificacionesBell';
import { useReportarProblema } from '../context/ReportarProblemaContext';
import { useDropdownAnimation } from '../hooks/useDropdownAnimation';
import { DROPDOWN_ANIMATION, DROPDOWN_BG } from './ui/tokens';
import { useAuth } from '../context/AuthContext';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { detectarPlataforma } from '../utils/detectarPlataforma';
import { correspondeInvitar, marcarInvitacionAtendida } from '../utils/invitacionInstalacion';
import { InstalarAppBanner } from './InstalarAppBanner';
import { InstalarAppDialog } from './InstalarAppDialog';

/**
 * Sobre qué se está operando: el lumisial activo, o "Administración". Siempre texto,
 * nunca botón — cambiar de lumisial vive en el menú del avatar ("Cambiar Lumisial"),
 * no acá (decisión revertida tras uso en producción; ver STATE.md, sesión 2026-08-19).
 */
interface ContextoIndicador {
  readonly etiqueta: string;
  readonly icon: React.ReactNode;
}

interface TopBarProps {
  contexto?: ContextoIndicador;
  onMenuClick?: () => void;
  showMenuButton?: boolean;
  isMainHome?: boolean;
  mainTitle?: string;
  onRelaunchTour?: () => void;
  helpHintPending?: boolean;
  onDismissHelpHint?: () => void;
  /**
   * El tour de bienvenida está cubriendo la pantalla. Solo lo pasa `Layout.tsx`,
   * el único lugar donde el tour existe; la franja de instalación espera su turno
   * para no quedar detrás del overlay y perderse.
   */
  tourActivo?: boolean;
}

/**
 * Tamaño de letra que cede solo cuando el nombre no entra, calculado con el ancho
 * real del contenedor (`cqi`) dividido por el largo del texto. Sin ResizeObserver
 * ni lectura de layout — spec, decisión 9.
 *
 * `container-type` va en el span que envuelve SOLO el texto, no en el que también
 * tiene el ícono: si el ícono comparte el contenedor medido, `100cqi` incluye su
 * ancho y la fórmula calcula un tamaño más grande del que en realidad entra —
 * descubierto verificando en vivo, truncaba en el caso real más largo pese al piso
 * de 12px.
 */
function ContextoIndicadorView({ etiqueta, icon }: ContextoIndicador) {
  const estiloAdaptativo = {
    '--largo-etiqueta': etiqueta.length,
    fontSize: 'clamp(12px, calc(100cqi / (var(--largo-etiqueta) * 0.55)), 14px)',
  } as React.CSSProperties;

  return (
    <div
      data-testid="contexto-indicador"
      className="flex min-w-0 flex-1 items-center gap-1.5 px-1 py-1 text-gray-900 font-semibold"
    >
      {icon}
      <span
        className="min-w-0 flex-1"
        style={{ containerType: 'inline-size' }}
        title={etiqueta}
      >
        <span className="block truncate" style={estiloAdaptativo}>{etiqueta}</span>
      </span>
    </div>
  );
}

export const TopBar = React.forwardRef<HTMLElement, TopBarProps>(({
  contexto,
  onMenuClick,
  showMenuButton = false,
  isMainHome = false,
  mainTitle,
  onRelaunchTour,
  helpHintPending = false,
  onDismissHelpHint,
  tourActivo = false,
}, ref) => {
  const { abrir: abrirReporte } = useReportarProblema();
  const helpDropdown = useDropdownAnimation();
  const helpRef = useRef<HTMLDivElement>(null);
  const { usuario } = useAuth();
  const { yaInstalada, instalar } = useInstallPrompt();
  const [dialogoInstalacionAbierto, setDialogoInstalacionAbierto] = useState(false);
  const [invitacionAtendidaAhora, setInvitacionAtendidaAhora] = useState(false);
  // La app no puede saber si el usuario terminó los pasos manuales (iOS, anclaje de
  // Firefox, acceso directo): al cerrar la ventana, un indicador guía de vuelta al
  // menú de ayuda por si quiere retomarlo, igual que el del tour de bienvenida.
  const [instalarHintPendiente, setInstalarHintPendiente] = useState(false);

  // El user agent no cambia durante la vida de la página.
  const entorno = useMemo(
    () => detectarPlataforma(navigator.userAgent, navigator.maxTouchPoints),
    [],
  );

  const idUsuario = usuario?.id ?? null;

  // Derivado en render, sin efecto: la franja se muestra mientras se cumplan las tres
  // condiciones y el usuario no la haya atendido. `yaInstalada` entra en la condición,
  // así que instalar la retira sola. `tourActivo` pasando a false la enciende sola.
  const invitacionVisible =
    !invitacionAtendidaAhora &&
    correspondeInvitar({ idUsuario, yaInstalada, tourActivo });

  /** Cerrarla, usar su botón o instalar: en los tres casos deja de aparecer para siempre. */
  const atenderInvitacion = () => {
    if (idUsuario) marcarInvitacionAtendida(idUsuario);
    setInvitacionAtendidaAhora(true);
  };

  const abrirInstalacion = async () => {
    const seAbrioElNativo = await instalar();
    if (!seAbrioElNativo) setDialogoInstalacionAbierto(true);
  };

  const instalarDesdeLaFranja = () => {
    atenderInvitacion();
    void abrirInstalacion();
  };

  // El indicador (punto pulsante) se apaga al CERRAR el menú, no al abrirlo: así queda
  // visible mientras el menú está desplegado (tanto en el ícono como junto a "Ver tour
  // inicial"), guiando al usuario a dónde hacer clic, y no desaparece antes de que lo vea.
  const cerrarMenuAyuda = () => {
    helpDropdown.close();
    onDismissHelpHint?.();
    setInstalarHintPendiente(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        helpDropdown.close();
        onDismissHelpHint?.();
        setInstalarHintPendiente(false);
      }
    };
    if (helpDropdown.isMounted) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [helpDropdown, onDismissHelpHint]);

  const alternarMenuAyuda = () => {
    if (helpDropdown.isOpen) {
      onDismissHelpHint?.();
      setInstalarHintPendiente(false);
    }
    helpDropdown.toggle();
  };

  let instalarBadge: React.ReactNode = null;
  if (yaInstalada) {
    instalarBadge = (
      <span className="ml-auto flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
        Instalada
      </span>
    );
  } else if (instalarHintPendiente) {
    instalarBadge = (
      <span
        aria-hidden="true"
        className="ml-auto w-2 h-2 bg-blue-500 rounded-full motion-safe:animate-pulse"
      />
    );
  }

  function renderZonaPrincipal() {
    if (isMainHome) {
      return (
        <div className="flex items-center gap-2">
          <HeartHandshake className="w-6 h-6 text-blue-600" />
          <h1 className="text-base lg:text-lg font-semibold text-gray-900 antialiased">
            {mainTitle || 'Gestión Administrativa Gnóstica'}
          </h1>
        </div>
      );
    }

    // Todo consumidor fuera de isMainHome pasa contexto (Layout, AdminLayout); el
    // guard es solo defensivo, no un camino real.
    if (!contexto) return null;
    return <ContextoIndicadorView {...contexto} />;
  }

  return (
    <>
    <header ref={ref} className="min-w-[320px] bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="flex items-center justify-between px-4 py-1.5 lg:px-6">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {showMenuButton && onMenuClick && (
            <button
              onClick={onMenuClick}
              className="lg:hidden p-0.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
          )}

          {renderZonaPrincipal()}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <div ref={helpRef} className="relative" data-tour="help-menu">
            <button
              type="button"
              onClick={alternarMenuAyuda}
              className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
              aria-label="Ayuda"
              title="Ayuda"
            >
              <HelpCircle className="w-5 h-5" />
              {(helpHintPending || instalarHintPendiente) && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full ring-2 ring-white motion-safe:animate-pulse" />
              )}
            </button>

            {helpDropdown.isMounted && (
              <div
                data-state={helpDropdown.dataState}
                className={`absolute right-0 top-full mt-2 w-56 rounded-xl shadow-xl z-50 overflow-hidden ${DROPDOWN_BG} ${DROPDOWN_ANIMATION}`}
                style={{ transformOrigin: 'top right' }}
                onAnimationEnd={helpDropdown.onAnimationEnd}
              >
                <button
                  type="button"
                  onClick={() => { cerrarMenuAyuda(); onRelaunchTour?.(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <PlayCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  Ver tour inicial
                  {helpHintPending && (
                    <span
                      aria-hidden="true"
                      className="ml-auto w-2 h-2 bg-blue-500 rounded-full motion-safe:animate-pulse"
                    />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { cerrarMenuAyuda(); void abrirInstalacion(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                >
                  <Download className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  Instalar app
                  {instalarBadge}
                </button>
                <button
                  type="button"
                  onClick={() => { cerrarMenuAyuda(); abrirReporte(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                >
                  <Bug className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  Reportar un problema
                </button>
              </div>
            )}
          </div>
          <NotificacionesBell />
          <UserProfileBadge />
        </div>
      </div>

      {invitacionVisible && (
        <InstalarAppBanner
          entorno={entorno}
          onInstalar={instalarDesdeLaFranja}
          onCerrar={atenderInvitacion}
        />
      )}
    </header>
    <InstalarAppDialog
      isOpen={dialogoInstalacionAbierto}
      onClose={() => { setDialogoInstalacionAbierto(false); setInstalarHintPendiente(true); }}
      entorno={entorno}
    />
    </>
  );
});

TopBar.displayName = 'TopBar';
